import { useQuery } from "@tanstack/react-query";
import type { UserWithOrgs } from "shared/schema";

/**
 * Sentinel returned by the profile query when GET /auth/user answers 403 because
 * the user's active firm was deactivated mid-session (tenant middleware's
 * ORG_DEACTIVATED). It is NOT a logged-out state: the session is still valid and
 * the user may belong to other firms, so the app routes them to the org picker
 * (switch firm / sign out) rather than the Landing page.
 */
export const ORG_DEACTIVATED = "ORG_DEACTIVATED" as const;

type AuthResult = UserWithOrgs | null | typeof ORG_DEACTIVATED;

const fetchUserProfile = async (): Promise<AuthResult> => {
  const response = await fetch("/api/auth/user");
  if (!response.ok) {
    // A 401 means the session is gone: treat as logged out (null → Landing).
    if (response.status === 401) {
      return null;
    }
    // A 403 carrying ORG_DEACTIVATED is the one 403 we route to the picker.
    // Every other 403 (and other error) keeps the old behaviour: it throws,
    // isError becomes true, and the user lands on Landing.
    if (response.status === 403) {
      const body = await response.json().catch(() => null);
      if (body && body.code === ORG_DEACTIVATED) {
        return ORG_DEACTIVATED;
      }
    }
    throw new Error("Network response was not ok");
  }
  return response.json();
};

export function useAuth() {
  const { data, isLoading, isError } = useQuery<AuthResult>({
    queryKey: ["user-profile"], // A descriptive key for this query
    queryFn: fetchUserProfile,
    retry: false,
    refetchOnWindowFocus: false, // Optional: prevent refetching on window focus
  });

  const orgDeactivated = data === ORG_DEACTIVATED;
  // Compare against the sentinel directly (not via `orgDeactivated`) so TS
  // narrows the string out and `user` stays cleanly UserWithOrgs | null.
  const user: UserWithOrgs | null = data && data !== ORG_DEACTIVATED ? data : null;

  return {
    user,
    isLoading,
    isAuthenticated: !!user && !isError,
    // The active firm was deactivated mid-session: not logged out, but has no
    // usable firm. The router sends this user to the org picker.
    orgDeactivated,
  };
}
